import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import RecordPage from "@/pages/RecordPage";
import RecipePage from "@/pages/RecipePage";
import ProfilePage from "@/pages/ProfilePage";
import ChefPage from "@/pages/ChefPage";
import MyRecipesPage from "@/pages/MyRecipesPage";
import DiscoverPage from "@/pages/DiscoverPage";
import FridgeScannerPage from "@/pages/FridgeScannerPage";
import DishScannerPage from "@/pages/DishScannerPage";
import EditRecipePage from "@/pages/EditRecipePage";
import WeeklyMenuPage from "@/pages/WeeklyMenuPage";
import MyReceiptsPage from "@/pages/MyReceiptsPage";
import WineCellarPage from "@/pages/WineCellarPage";
import WebRecipePage from "@/pages/WebRecipePage";
// Business B2B Pages
import BusinessRegisterPage from "@/pages/BusinessRegisterPage";
import BusinessDashboardPage from "@/pages/BusinessDashboardPage";
import BusinessProfilePage from "@/pages/BusinessProfilePage";
import BusinessCatalogPage from "@/pages/BusinessCatalogPage";
import BusinessCampaignsPage from "@/pages/BusinessCampaignsPage";
import BusinessAnalyticsPage from "@/pages/BusinessAnalyticsPage";
import BusinessOrdersPage from "@/pages/BusinessOrdersPage";
import BusinessPickupSlotsPage from "@/pages/BusinessPickupSlotsPage";
import BusinessSubscriptionPage from "@/pages/BusinessSubscriptionPage";
import BusinessCustomersPage from "@/pages/BusinessCustomersPage";
import StorePublicPage from "@/pages/StorePublicPage";
import RestaurantMenuPage from "@/pages/RestaurantMenuPage";
import DailySpecialPage from "@/pages/DailySpecialPage";
import AntiWasteBoxPage from "@/pages/AntiWasteBoxPage";
import DishPhotosManagePage from "@/pages/DishPhotosManagePage";
import BusinessCouponsPage from "@/pages/BusinessCouponsPage";
import BusinessBoostPage from "@/pages/BusinessBoostPage";
import CouponsPage from "@/pages/CouponsPage";
import GuidePage from "@/pages/GuidePage";
// Public Pages
import MealRatingPage from "@/pages/MealRatingPage";
import JoinPage from "@/pages/JoinPage";
import MealDetailPage from "@/pages/MealDetailPage";
// Social & Points Pages
import FriendsPage from "@/pages/FriendsPage";
import DietaryProfilePage from "@/pages/DietaryProfilePage";
import CreateMealPage from "@/pages/CreateMealPage";
import PointsPage from "@/pages/PointsPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import MyMealsPage from "@/pages/MyMealsPage";
import MyRatingsPage from "@/pages/MyRatingsPage";
import PublicProfilePage from "@/pages/PublicProfilePage";
import OnboardingPage from "@/pages/OnboardingPage";
// Admin Pages
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import NotificationsPage from "@/pages/NotificationsPage";
// Legal & Support Pages
import { CGVPage, MentionsLegalesPage, ConfidentialitePage } from "@/pages/LegalPages";
import FAQPage from "@/pages/FAQPage";
import SupportPage from "@/pages/SupportPage";
// Stores Directory
import StoresDirectoryPage from "@/pages/StoresDirectoryPage";
import StoreDetailPage from "@/pages/StoreDetailPage";
// Shop
import ShopPage from "@/pages/ShopPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ShopSuccessPage from "@/pages/ShopSuccessPage";
import OrdersPage from "@/pages/OrdersPage";
import PurchaseStatsPage from "@/pages/PurchaseStatsPage";
import HouseholdPage from "@/pages/HouseholdPage";

// Components
import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("token");
          setToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (pseudo, email, password, postalCode) => {
    const response = await axios.post(`${API}/auth/register`, { 
      pseudo, 
      email, 
      password,
      postal_code: postalCode,
      is_chef: true 
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch {}
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          {/* Business Registration (outside Layout - not logged in yet) */}
          <Route path="/business/register" element={<BusinessRegisterPage />} />
          {/* Store Public Page (outside Layout - accessible to everyone) */}
          <Route path="/store/:storeId" element={<StorePublicPage />} />
          {/* Meal Rating (public - no auth needed) */}
          <Route path="/rate/:invitationId" element={<MealRatingPage />} />
          {/* Friend Invite (public - no auth needed) */}
          <Route path="/join/:inviteId" element={<JoinPage />} />
          {/* Onboarding (after registration) */}
          <Route path="/onboarding" element={<OnboardingPage />} />
          {/* Shared Meal (public - no auth needed for viewing) */}
          <Route path="/meals/:mealId" element={<MealDetailPage />} />
          {/* Main Layout - includes both user and business routes with different navigation */}
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="discover" element={<DiscoverPage />} />
            {/* Business B2B Routes (inside Layout for navigation) */}
            <Route path="business/dashboard" element={<BusinessDashboardPage />} />
            <Route path="business/profile" element={<BusinessProfilePage />} />
            <Route path="business/catalog" element={<BusinessCatalogPage />} />
            <Route path="business/campaigns" element={<BusinessCampaignsPage />} />
            <Route path="business/campaigns/new" element={<BusinessCampaignsPage />} />
            <Route path="business/analytics" element={<BusinessAnalyticsPage />} />
            <Route path="business/orders" element={<BusinessOrdersPage />} />
            <Route path="business/pickup-slots" element={<BusinessPickupSlotsPage />} />
            <Route path="business/customers" element={<BusinessCustomersPage />} />
            <Route path="business/menu" element={<RestaurantMenuPage />} />
            <Route path="business/daily-special" element={<DailySpecialPage />} />
            <Route path="business/anti-waste" element={<AntiWasteBoxPage />} />
            <Route path="business/dish-photos" element={<DishPhotosManagePage />} />
            <Route path="business/coupons" element={<BusinessCouponsPage />} />
            <Route path="business/boost" element={<BusinessBoostPage />} />
            <Route path="business/boost/success" element={<BusinessBoostPage />} />
            <Route path="business/subscription" element={<BusinessSubscriptionPage />} />
            <Route path="business/subscription/success" element={<BusinessSubscriptionPage />} />
            {/* User Routes */}
            <Route path="recipe/:id" element={<RecipePage />} />
            <Route path="recipe/:id/edit" element={
              <ProtectedRoute>
                <EditRecipePage />
              </ProtectedRoute>
            } />
            <Route path="chef/:id" element={<ChefPage />} />
            <Route path="fridge-scanner" element={
              <ProtectedRoute>
                <FridgeScannerPage />
              </ProtectedRoute>
            } />
            <Route path="dish-scanner" element={
              <ProtectedRoute>
                <DishScannerPage />
              </ProtectedRoute>
            } />
            <Route path="web-recipe/:recipeId" element={
              <ProtectedRoute>
                <WebRecipePage />
              </ProtectedRoute>
            } />
            <Route path="weekly-menu" element={
              <ProtectedRoute>
                <WeeklyMenuPage />
              </ProtectedRoute>
            } />
            <Route path="my-receipts" element={
              <ProtectedRoute>
                <MyReceiptsPage />
              </ProtectedRoute>
            } />
            <Route path="purchase-stats" element={
              <ProtectedRoute>
                <PurchaseStatsPage />
              </ProtectedRoute>
            } />
            <Route path="household" element={
              <ProtectedRoute>
                <HouseholdPage />
              </ProtectedRoute>
            } />
            <Route path="wine-cellar" element={
              <ProtectedRoute>
                <WineCellarPage />
              </ProtectedRoute>
            } />
            <Route path="record" element={
              <ProtectedRoute>
                <RecordPage />
              </ProtectedRoute>
            } />
            <Route path="my-recipes" element={
              <ProtectedRoute>
                <MyRecipesPage />
              </ProtectedRoute>
            } />
            <Route path="profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            {/* Friends & Social Routes */}
            <Route path="user/:userId" element={<PublicProfilePage />} />
            <Route path="friends" element={
              <ProtectedRoute>
                <FriendsPage />
              </ProtectedRoute>
            } />
            <Route path="dietary-profile" element={
              <ProtectedRoute>
                <DietaryProfilePage />
              </ProtectedRoute>
            } />
            <Route path="meals/new" element={
              <ProtectedRoute>
                <CreateMealPage />
              </ProtectedRoute>
            } />
            <Route path="points" element={
              <ProtectedRoute>
                <PointsPage />
              </ProtectedRoute>
            } />
            <Route path="leaderboard" element={
              <ProtectedRoute>
                <LeaderboardPage />
              </ProtectedRoute>
            } />
            <Route path="my-meals" element={
              <ProtectedRoute>
                <MyMealsPage />
              </ProtectedRoute>
            } />
            <Route path="my-ratings" element={
              <ProtectedRoute>
                <MyRatingsPage />
              </ProtectedRoute>
            } />
            {/* Admin Routes */}
            <Route path="admin" element={
              <ProtectedRoute>
                <AdminDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="admin/dashboard" element={
              <ProtectedRoute>
                <AdminDashboardPage />
              </ProtectedRoute>
            } />
            {/* Notifications */}
            <Route path="notifications" element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            } />
            {/* Legal & Support */}
            <Route path="legal/cgv" element={<CGVPage />} />
            <Route path="legal/mentions" element={<MentionsLegalesPage />} />
            <Route path="legal/confidentialite" element={<ConfidentialitePage />} />
            <Route path="faq" element={<FAQPage />} />
            <Route path="support" element={<SupportPage />} />
            {/* Stores Directory */}
            <Route path="stores" element={<StoresDirectoryPage />} />
            <Route path="stores/:storeId" element={<StoreDetailPage />} />
            <Route path="coupons" element={<CouponsPage />} />
            <Route path="coupons/success" element={<CouponsPage />} />
            <Route path="guide" element={<GuidePage />} />
            <Route path="shop" element={<ShopPage />} />
            <Route path="shop/success" element={<ShopSuccessPage />} />
            <Route path="shop/orders" element={<OrdersPage />} />
            <Route path="shop/:productId" element={<ProductDetailPage />} />
          </Route>
        </Routes>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
